import { addDays, format } from '@if/core/date'
import { EApplicantType, EDocumentStatus, EDocumentType, ERequiredFrom } from '@if/core/types'
import { RequiredExternalStep } from '@src/types/CreditApplicationSchema'
import { RequiredDocument } from '@src/types/RequiredDocument'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export type IdentityVerificationByApplicant = Partial<Record<EApplicantType, RequiredExternalStep | null>>

/**
 * A photo ID locked to the credit agent is only actionable by the merchant when the applicant also
 * has an identity verification step: the ID then arrives through Vouched rather than an upload.
 * Any other credit-agent-locked document stays out of the merchant's hands entirely.
 *
 * Gated on EnableVouchedForThinCreditB2B so that turning the switch off restores the previous
 * behaviour for applications that already have a credit-agent-locked photo ID: the row is hidden
 * again rather than falling back to an upload field.
 */
export function isIdentityVerificationDocument(
  documentRequired: RequiredDocument,
  identityVerification: RequiredExternalStep | null | undefined,
  isThinFileVouchedEnabled: boolean,
): boolean {
  return (
    isThinFileVouchedEnabled &&
    documentRequired.requiredFrom === ERequiredFrom.CreditAgent &&
    documentRequired.typeId === EDocumentType.ValidPhotoId &&
    !!identityVerification
  )
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'

export function useDocumentStatus(documentRequired: RequiredDocument) {
  const { t } = useTranslation()

  return useMemo(() => {
    let statusMessage: string
    let chipColor: ChipColor

    switch (documentRequired.status) {
      case EDocumentStatus.Approved:
        statusMessage = t('EDocumentStatus.approved', { ns: 'enum' })
        chipColor = 'success'
        break
      case EDocumentStatus.AwaitingApproval:
        statusMessage = t('EDocumentStatus.awaitingApproval', { ns: 'enum' })
        chipColor = 'info'
        break
      case EDocumentStatus.Refused:
        statusMessage = t('EDocumentStatus.refused', { ns: 'enum' })
        chipColor = 'error'
        break
      case EDocumentStatus.Incomplete:
        statusMessage = t('EDocumentStatus.incomplete', { ns: 'enum' })
        chipColor = 'warning'
        break
      default:
        statusMessage = t('viewCreditApplication.documentsAwaitingUpload')
        chipColor = 'warning'
    }
    return { statusMessage, chipColor }
  }, [documentRequired.status, t])
}

export function useFullNameText(documentRequired: RequiredDocument) {
  const { t } = useTranslation()

  const fullNameText = useMemo(() => {
    let applicantSuffix = ''
    if (documentRequired.applicantType === EApplicantType.Applicant) {
      applicantSuffix = ` - ${t('common.applicant')}`
    } else if (documentRequired.applicantType === EApplicantType.CoApplicant) {
      applicantSuffix = ` - ${t('common.coApplicant')}`
    }

    return t('common.currentLanguage') === 'Fr'
      ? `${documentRequired.nameFr}${applicantSuffix}`
      : `${documentRequired.nameEn}${applicantSuffix}`
  }, [documentRequired, t])

  return fullNameText
}

export function canSendOrDeleteHighEstimateInvoice(firstPaymentOn: Date) {
  const maxDateToSendInvoice = format(addDays(firstPaymentOn, -5), 'yyyy-MM-dd')
  const today = format(new Date(), 'yyyy-MM-dd')
  return today <= maxDateToSendInvoice
}
